import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
  Activity,
  ArrowRight,
  Check,
  ChevronRight,
  Clock,
  FileText,
  ShieldAlert,
  X,
  Zap,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  DealAskRobReviewCard,
  DealAskRobReviewCardForDeal,
} from "@/components/deals/DealAskRobReviewCard"
import { DealProgressTracker } from "@/components/deals/DealProgressTracker"
import { MarketEvidenceSection } from "@/components/deals/evidence/MarketEvidenceSection"
import { AiCreditUsageLink } from "@/components/shared/AiCreditUsageLink"
import { getDealRiskFlags } from "@/lib/api"
import { looksPersistedDealId } from "@/lib/persisted-deal"
import { useSession } from "@/lib/session-context"
import { cn } from "@/lib/utils"

const PRIORITY_ACTION_ICONS = {
  add_floorplan: FileText,
  add_media: FileText,
  complete_current_income_schedule: FileText,
  log_first_vendor_circumstances: Zap,
  run_concept_engine: Zap,
  submit_offer: ArrowRight,
}

const EMPTY_RISK_FLAG_SUMMARY = { activeCount: 0, highestSeverity: null }

const RISK_FLAG_SEVERITY_STYLES = {
  critical: "border-destructive/20 bg-destructive/10 text-destructive",
  high: "border-destructive/20 bg-destructive/10 text-destructive",
  medium: "border-chart-4/20 bg-chart-4/10 text-chart-4",
  low: "border-primary/20 bg-primary/10 text-primary",
}

function titleize(value) {
  return value
    .toString()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ")
}

function flagCountLabel(count) {
  if (count === 1) return "1 Flag"
  return `${count} Flags`
}

function riskFlagClassName(severity) {
  return RISK_FLAG_SEVERITY_STYLES[severity] ?? RISK_FLAG_SEVERITY_STYLES.low
}

function commercialAlignmentValue(deal) {
  return deal.commercialAlignmentLabel || (Number.isFinite(deal.commercialAlignmentScore) ? "Available" : "Building")
}

function commercialAlignmentDescription(deal) {
  if (!Number.isFinite(deal.commercialAlignmentScore)) {
    return "Commercial alignment will calculate after appraisal."
  }

  let marginLabel = ""
  if (Number.isFinite(deal.commercialGapPercent)) {
    const direction = deal.commercialGapPercent < 0 ? "Headroom" : "Gap"
    marginLabel = ` ${direction} ${Math.abs(deal.commercialGapPercent)}%.`
  }

  return `Commercial alignment score ${deal.commercialAlignmentScore}.${marginLabel}`
}

function commercialAlignmentPercent(deal) {
  return Number.isFinite(deal.commercialAlignmentScore) ? deal.commercialAlignmentScore : 10
}

function vendorMotivationValue(deal) {
  if (deal.vendorIntelligenceStatus === "queued") return "Queued"
  if (deal.vendorIntelligenceStatus === "running") return "Analysing"
  if (deal.vendorIntelligenceStatus === "failed") return "Needs retry"
  if (deal.vendorIntelligenceStatus === "current") return deal.vendorMotivationLabel || "Unknown"

  return "Not analysed"
}

function vendorMotivationDescription(deal) {
  if (deal.vendorIntelligenceStatus === "queued") return "Vendor evidence is queued for analysis."
  if (deal.vendorIntelligenceStatus === "running") return "Sentinel is analysing vendor evidence."
  if (deal.vendorIntelligenceStatus === "failed") return "Analysis needs to be retried from the Vendor tab."
  if (deal.vendorIntelligenceStatus === "current") {
    return deal.vendorMotivationSummary || "No clear motivation signal has been identified."
  }

  return "Add vendor circumstances, then analyse them from the Vendor tab."
}

function confidenceValue(deal) {
  return titleize(deal.scoreConfidence || "low")
}

function formatStrategy(strategy) {
  if (!strategy) return ""
  if (typeof strategy === "string") return strategy
  return strategy.title || strategy.name || strategy.label || strategy.id || ""
}

function formatAppraisal(results) {
  if (!results) return null
  if (typeof results === "string") return { summary: results }

  return {
    maxPrice: results.maxPrice,
    targetClose: results.targetClose,
    summary: results.summary || results.verdict || results.margin,
  }
}

function formatNegotiation(status) {
  if (!status) return null
  if (typeof status === "string") return { status }

  return {
    latestOffer: status.latestOffer,
    walkAway: status.walkAway,
    status: status.status || status.summary || status.stage,
    agreedPrice: status.agreedPrice,
    agreedDate: status.agreedDate,
    lossReason: status.lossReason,
  }
}

export function DealOverviewTab({
  deal,
  maxUnlockedStage,
  selectedStrategy,
  appraisalResults,
  negotiationStatus,
  priorityActions = [],
  onCompletePriority,
  onDismissPriority,
  askRob,
  onProgressStepClick,
  onSnoozePriority,
  onSuggestionAccepted,
}) {
  const { currentUser, csrfToken } = useSession()
  const canUsePersistedDealApi = Boolean(currentUser && looksPersistedDealId(deal.id))
  const strategyLabel = formatStrategy(selectedStrategy)
  const appraisal = formatAppraisal(appraisalResults)
  const negotiation = formatNegotiation(negotiationStatus)
  const [riskFlags, setRiskFlags] = useState([])
  const [riskSummary, setRiskSummary] = useState(EMPTY_RISK_FLAG_SUMMARY)
  const [riskFlagsError, setRiskFlagsError] = useState(null)
  const [priorityActionError, setPriorityActionError] = useState(null)
  const [pendingPriorityId, setPendingPriorityId] = useState(null)
  const displayedRiskCount = riskSummary.activeCount ?? riskFlags.length
  const displayedRiskFlags = riskFlags.slice(0, 3)
  const displayedActions = priorityActions.map((action, index) => ({
    id: action.id,
    title: action.action,
    description: action.reason,
    icon: PRIORITY_ACTION_ICONS[action.actionKey] ?? Zap,
    isPrimary: index === 0,
    path: action.path,
  }))

  const runPriorityAction = async (action, callback) => {
    if (!action.id || !callback) return

    setPendingPriorityId(action.id)
    setPriorityActionError(null)
    try {
      await callback(action.id)
    } catch (error) {
      setPriorityActionError(error.message)
    } finally {
      setPendingPriorityId(null)
    }
  }

  useEffect(() => {
    if (!canUsePersistedDealApi) return undefined

    let ignore = false

    getDealRiskFlags(deal.id, { csrfToken })
      .then((payload) => {
        if (ignore) return

        setRiskFlags(payload.flags ?? [])
        setRiskSummary(payload.summary ?? EMPTY_RISK_FLAG_SUMMARY)
        setRiskFlagsError(null)
      })
      .catch((error) => {
        if (!ignore) setRiskFlagsError(error.message)
      })

    return () => { ignore = true }
  }, [csrfToken, deal.id, canUsePersistedDealApi])

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_18.75rem]">
      <div className="space-y-8">
        <section className="space-y-4">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-foreground">
              <Zap className="h-4 w-4 text-primary" />
              Priority Actions
            </h2>
            <p className="ml-6 mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground/70">
              Powered by Execution Engine
            </p>
          </div>

          <div className="space-y-3">
            {displayedActions.length > 0 ? displayedActions.map((action) => {
              const Icon = action.icon

              return (
                <div
                  key={action.id ?? action.title}
                  className="flex min-h-20 w-full items-stretch rounded-xl border border-border bg-background shadow-xs"
                >
                  {action.path ? (
                    <Button
                      variant="ghost"
                      asChild
                      className="h-auto min-w-0 flex-1 justify-between rounded-xl px-4 py-4 text-left"
                    >
                      <Link to={action.path}>
                        <PriorityActionContent
                          Icon={Icon}
                          action={action}
                        />
                      </Link>
                    </Button>
                  ) : (
                    <span className="flex min-w-0 flex-1 items-center justify-between px-4 py-4">
                      <PriorityActionContent
                        Icon={Icon}
                        action={action}
                      />
                    </span>
                  )}
                  {action.id && (onCompletePriority || onDismissPriority || onSnoozePriority) ? (
                    <span className="flex shrink-0 items-center gap-1 pr-3">
                      {onCompletePriority ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          disabled={pendingPriorityId === action.id}
                          aria-label={`Complete ${action.title}`}
                          title="Mark complete"
                          onClick={() => runPriorityAction(action, onCompletePriority)}
                        >
                          <Check />
                        </Button>
                      ) : null}
                      {onSnoozePriority ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          disabled={pendingPriorityId === action.id}
                          aria-label={`Snooze ${action.title} for 24 hours`}
                          title="Snooze for 24 hours"
                          onClick={() => runPriorityAction(action, onSnoozePriority)}
                        >
                          <Clock />
                        </Button>
                      ) : null}
                      {onDismissPriority ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          disabled={pendingPriorityId === action.id}
                          aria-label={`Dismiss ${action.title}`}
                          title="Dismiss permanently"
                          onClick={() => runPriorityAction(action, onDismissPriority)}
                        >
                          <X />
                        </Button>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              )
            }) : (
              <div className="rounded-xl border border-border bg-card px-4 py-5 text-sm text-muted-foreground">
                No priority actions right now.
              </div>
            )}
            {priorityActionError ? (
              <p className="text-xs font-medium text-destructive">{priorityActionError}</p>
            ) : null}
          </div>
        </section>

        <DealProgressTracker maxUnlockedStage={maxUnlockedStage} onStepClick={onProgressStepClick} />

        {canUsePersistedDealApi && deal.evidenceContext ? (
          <MarketEvidenceSection dealId={deal.id} csrfToken={csrfToken} />
        ) : null}

        {askRob ? (
          <DealAskRobReviewCard askRob={askRob} />
        ) : (
          <DealAskRobReviewCardForDeal
            deal={deal}
            onSuggestionAccepted={onSuggestionAccepted}
          />
        )}
      </div>

      <aside className="flex flex-col gap-6">
        <section className="rounded-xl border border-border bg-card text-card-foreground shadow-sm">
          <div className="p-6 pb-5">
            <h2 className="mb-6 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-foreground">
              <Activity className="h-4 w-4" />
              Deal Intelligence
            </h2>

            <h3 className="mb-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Data Intelligence
            </h3>

            <div className="space-y-4">
              <SignalMeter
                label="Margin Potential"
                value={commercialAlignmentValue(deal)}
                percent={commercialAlignmentPercent(deal)}
              />
              <p className="text-xs leading-relaxed text-muted-foreground/80">
                {commercialAlignmentDescription(deal)}
              </p>

              <hr className="border-border" />

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-muted-foreground">Vendor Motivation</span>
                  <span className="font-bold text-chart-4">{vendorMotivationValue(deal)}</span>
                </div>
                <p className="border-l-2 border-chart-4/50 pl-2 text-xs leading-relaxed text-muted-foreground/80">
                  {vendorMotivationDescription(deal)}
                </p>
              </div>

              <hr className="border-border" />

              <div className="space-y-2">
                <div className="mb-2 flex items-center justify-between text-xs">
                  <span className="font-medium text-muted-foreground">Risk Flags</span>
                  <span className={cn(
                    "flex items-center gap-1 font-bold",
                    displayedRiskCount > 0 ? "text-destructive" : "text-primary",
                  )}
                  >
                    <ShieldAlert className="h-3 w-3" />
                    {flagCountLabel(displayedRiskCount)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {displayedRiskFlags.length > 0 ? displayedRiskFlags.map((flag) => (
                    <span
                      key={flag.id}
                      className={cn(
                        "inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-bold uppercase tracking-wide",
                        riskFlagClassName(flag.severity),
                      )}
                    >
                      {flag.name}
                    </span>
                  )) : (
                    <span className="rounded border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-primary">
                      None Captured
                    </span>
                  )}
                </div>
                {riskFlagsError ? (
                  <p className="text-[10px] font-medium text-destructive">
                    {riskFlagsError} <AiCreditUsageLink error={riskFlagsError} />
                  </p>
                ) : null}
              </div>

              <hr className="border-border" />

              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-muted-foreground">Confidence Level</span>
                <span className="font-bold text-chart-4">{confidenceValue(deal)}</span>
              </div>
            </div>
          </div>

        </section>

        <ConditionalStateBlocks
          strategyLabel={strategyLabel}
          appraisal={appraisal}
          negotiation={negotiation}
        />
      </aside>
    </div>
  )
}

function PriorityActionContent({ Icon, action }) {
  return (
    <>
      <span className="flex min-w-0 items-center gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground group-hover:text-foreground">
          <Icon className={cn("h-5 w-5", action.isPrimary && "text-primary")} />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-base font-bold text-foreground">
            {action.title}
          </span>
          {action.description ? (
            <span className="mt-1 block truncate text-xs font-normal text-muted-foreground">
              {action.description}
            </span>
          ) : null}
        </span>
      </span>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <ChevronRight className="h-4 w-4" />
      </span>
    </>
  )
}

function SignalMeter({ label, value, percent = 80 }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-muted-foreground">{label}</span>
        <span className="font-bold text-primary">{value}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${Math.max(0, Math.min(percent, 100))}%` }}
        />
      </div>
    </div>
  )
}

function ConditionalStateBlocks({ strategyLabel, appraisal, negotiation }) {
  const appraisalRows = [
    ["Max price", appraisal?.maxPrice],
    ["Target close", appraisal?.targetClose],
    ["Summary", appraisal?.summary],
  ].filter(([, value]) => value != null && value !== "")
  const negotiationRows = [
    ["Latest offer", negotiation?.latestOffer],
    ["Walk away", negotiation?.walkAway],
    ["Status", negotiation?.status],
    ["Agreed price", negotiation?.agreedPrice],
    ["Date agreed", negotiation?.agreedDate],
    ["Loss reason", negotiation?.lossReason],
  ].filter(([, value]) => value != null && value !== "")

  return (
    <div className="space-y-4">
      {strategyLabel ? (
        <StateBlock label="Selected Strategy" value={strategyLabel} />
      ) : null}
      {appraisalRows.length ? (
        <StateBlock label="Deal Numbers">
          <StateRows rows={appraisalRows} />
        </StateBlock>
      ) : null}
      {negotiationRows.length ? (
        <StateBlock label="Negotiation Status">
          <StateRows rows={negotiationRows} />
        </StateBlock>
      ) : null}
    </div>
  )
}

function StateBlock({ label, value, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 text-card-foreground shadow-sm">
      <h3 className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </h3>
      {children ?? <p className="min-w-0 break-words text-sm font-bold text-foreground">{value}</p>}
    </div>
  )
}

function StateRows({ rows }) {
  return (
    <dl className="space-y-2">
      {rows.map(([label, value]) => (
        <div key={label} className="flex min-w-0 items-start justify-between gap-3 text-sm">
          <dt className="shrink-0 text-muted-foreground">{label}</dt>
          <dd className="min-w-0 flex-1 break-words text-right font-bold text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  )
}
